import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchHours,
  fetchHour,
  updateHour,
  createHour,
  deleteHour,
} from "../api/hour";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteHourAsync = createAsyncThunk(
  "hours/deleteHourAsync",
  async (id, { rejectWithValue }) => {
    try {
      return await deleteHour(id);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_hour");
    }
  }
);

export const createHourAsync = createAsyncThunk(
  "hours/createHourAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createHour(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_hour");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_hour");
    }
  }
);

export const updateHourAsync = createAsyncThunk(
  "hours/updateHourAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateHour(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_hour");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_hour");
    }
  }
);

export const getHoursAsync = createAsyncThunk(
  "hours/getHoursAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchHours(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_hours");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_hours");
    }
  }
);

export const getHourAsync = createAsyncThunk(
  "hours/getHourAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchHour(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_hour");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_hour");
    }
  }
);

const hourSlice = createSlice({
  name: "hours",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getHoursAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getHoursAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newestHoursCount = action.payload.newCount;
      })
      .addCase(getHoursAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente timene.");
      })
      .addCase(updateHourAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateHourAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.payload?.id;
          const index = draftState.data.findIndex((type) => type.id === id);
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateHourAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere timen.");
      })
      .addCase(createHourAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createHourAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(createHourAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette timen.");
      })
      .addCase(deleteHourAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteHourAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.meta?.arg ?? action.payload?.id;
          if (id != null) {
            draftState.data = draftState.data.filter((row) => row.id !== id);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteHourAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette timen.");
      })
      .addCase(getHourAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getHourAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.hour = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getHourAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente timen.");
      });
  },
});

// export const {} = customerSlice.actions;

export const { reducer } = hourSlice;

export default hourSlice;
